import styled from 'styled-components'
import { useState, useEffect } from 'react'
import resolve from '../../../../common/utils/object/resolve.js'
import useLocale from '../../hooks/useLocale'
import LineActions from '../listView/LineActions'
import SelectionCheckbox from '../listView/SelectionCheckbox'
import { saveToLS, getFromLS } from '../../utils/localStore/localStore'
import {
    TableList,
    TableListItem,
    ActionsOverlay,
    TableCell,
    NoData,
    hasValidData
} from './Common'

import * as BsIcons from 'react-icons/bs'
import DisplayElement from '../display/DisplayElement'

const ChildTableListItem = styled(TableListItem)`
    padding-left: 48px;
    font-style: italic;
    opacity: 0.8;
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.table.active : theme.table.childBackground || 'transparent'
    };
`

const ExpansionIndicator = styled.div`
    margin-right: 8px;
    transition: transform 0.2s ease;
    transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
    display: flex;
    align-items: center;
`

const TableRowsHierarchical = ({
    endpoint,
    result,
    columns,
    slug = '',
    tableKey = 'id',
    path = '',
    lineActions,
    onAction,
    listActions,
    selectedItems,
    onItemSelect,
    hierarchicalConfig,
    filter
}) => {
    const { lf } = useLocale()
    const [expandedParents, setExpandedParents] = useState({})

    const hasListActions = listActions && listActions.menuItems && listActions.menuItems.length > 0

    // N-level mode: config has parentValue key (even if it's null)
    const isNLevelMode = hierarchicalConfig && 'parentValue' in hierarchicalConfig

    useEffect(() => {
        if (hierarchicalConfig) {
            const storageKey = `expandedParents_${endpoint}_${filter}`
            const savedState = getFromLS(storageKey)
            if (savedState) setExpandedParents(savedState)
        }
    }, [endpoint, filter, hierarchicalConfig])

    useEffect(() => {
        if (hierarchicalConfig) {
            const storageKey = `expandedParents_${endpoint}_${filter}`
            saveToLS(storageKey, null, expandedParents)
        }
    }, [expandedParents, endpoint, filter, hierarchicalConfig])

    const toggleParent = (parentId) => {
        setExpandedParents(prev => {
            const newState = { ...prev, [parentId]: !prev[parentId] }
            Object.keys(newState).forEach(key => {
                if (!newState[key]) delete newState[key]
            })
            return newState
        })
    }

    if (!hasValidData(result)) {
        return NoData
    }

    const allItems = result.data.pages.flatMap(page => page.data || [])

    // ---- N-level mode: parentField + parentValue ----
    if (isNLevelMode) {
        const { parentField, parentValue } = hierarchicalConfig

        const childrenByParent = {}
        allItems.forEach(item => {
            const pid = item[parentField]
            if (pid != null && pid !== '') {
                if (!childrenByParent[pid]) childrenByParent[pid] = []
                childrenByParent[pid].push(item)
            }
        })

        // Items whose parentField matches parentValue (== handles null/undefined)
        const rootItems = allItems.filter(item => item[parentField] == parentValue)

        const renderItem = (item, depth) => {
            const id = item[tableKey]
            const isSelected = selectedItems?.has(id)
            const children = childrenByParent[id] || []
            const hasChildren = children.length > 0
            const isExpanded = !!expandedParents[id]

            return (
                <div key={id}>
                    <TableListItem
                        as="div"
                        $isSelected={isSelected}
                        style={{ paddingLeft: `${depth * 1.5 + 1.5}rem` }}
                        onClick={() => hasChildren && toggleParent(id)}
                    >
                        {hasListActions && (
                            <TableCell $len="18px" $svgMargin="0">
                                <SelectionCheckbox
                                    state={isSelected ? 'selected' : 'none'}
                                    onChange={(opts) => onItemSelect && onItemSelect(id, opts)}
                                />
                            </TableCell>
                        )}
                        <TableCell $len="24px" $svgMargin="0">
                            {hasChildren && (
                                <ExpansionIndicator $expanded={isExpanded}>
                                    <BsIcons.BsChevronRight />
                                </ExpansionIndicator>
                            )}
                        </TableCell>
                        {columns.map((col, j) => (
                            <TableCell key={j} $len={col.width} $shrink={col.shrink}>
                                <p>
                                    <DisplayElement
                                        string={resolve(col.id, item)}
                                        type={col.type}
                                        col={col}
                                        item={item}
                                    />
                                </p>
                            </TableCell>
                        ))}
                        {lineActions && (
                            <ActionsOverlay onClick={(e) => e.stopPropagation()}>
                                <LineActions lineActions={lineActions} item={item} onAction={onAction} />
                            </ActionsOverlay>
                        )}
                    </TableListItem>
                    {isExpanded && children.map(child => renderItem(child, depth + 1))}
                </div>
            )
        }

        return (
            <TableList>
                {rootItems.map(item => renderItem(item, 0))}
            </TableList>
        )
    }

    // ---- Legacy 2-level mode: field + value + parentField ----
    const parentItems = allItems.filter(item =>
        item[hierarchicalConfig.field] !== hierarchicalConfig.value
    )

    const visibleOrderedIds = (() => {
        const ids = []
        parentItems.forEach((parentItem) => {
            ids.push(parentItem.id)
            if (expandedParents[parentItem.id]) {
                const childItems = allItems.filter(item =>
                    item[hierarchicalConfig.field] === hierarchicalConfig.value &&
                    item[hierarchicalConfig.parentField] === parentItem.id
                )
                childItems.forEach(child => ids.push(child.id))
            }
        })
        return ids
    })()

    return (
        <TableList>
            {parentItems.map((parentItem, parentIndex) => {
                const isParentSelected = selectedItems && selectedItems.has(parentItem.id)
                const isExpanded = expandedParents[parentItem.id]

                const childItems = allItems.filter(item =>
                    item[hierarchicalConfig.field] === hierarchicalConfig.value &&
                    item[hierarchicalConfig.parentField] === parentItem.id
                )

                return (
                    <div key={parentItem.id}>
                        <TableListItem
                            data-testid={`${endpoint}.list.${parentIndex + 1}`}
                            as="div"
                            $isSelected={isParentSelected}
                            onClick={() => toggleParent(parentItem.id)}
                        >
                            {hasListActions && (
                                <TableCell $len="18px" $svgMargin="0">
                                    <SelectionCheckbox
                                        state={isParentSelected ? 'selected' : 'none'}
                                        onChange={(opts) => onItemSelect && onItemSelect(parentItem.id, { ...opts, orderedIds: visibleOrderedIds })}
                                    />
                                </TableCell>
                            )}
                            <TableCell $len="24px" $svgMargin="0">
                                <ExpansionIndicator $expanded={isExpanded}>
                                    <BsIcons.BsChevronRight />
                                </ExpansionIndicator>
                            </TableCell>
                            {columns.map((col, j) => (
                                <TableCell key={j} $len={col.width} $shrink={col.shrink}>
                                    <p data-testid={`${endpoint}.list.${parentIndex + 1}.${col.id}`}>
                                        <DisplayElement
                                            string={resolve(col.id, parentItem)}
                                            type={col.type}
                                            col={col}
                                            item={parentItem}
                                        />
                                    </p>
                                </TableCell>
                            ))}
                            {lineActions && (
                                <ActionsOverlay onClick={(e) => e.stopPropagation()}>
                                    <LineActions
                                        lineActions={lineActions}
                                        item={parentItem}
                                        onAction={onAction}
                                    />
                                </ActionsOverlay>
                            )}
                        </TableListItem>

                        {isExpanded && childItems.map((childItem, childIndex) => {
                            const isChildSelected = selectedItems && selectedItems.has(childItem.id)

                            return (
                                <ChildTableListItem
                                    key={childItem.id}
                                    data-testid={`${endpoint}.list.${parentIndex + 1}.child.${childIndex + 1}`}
                                    as="div"
                                    $isSelected={isChildSelected}
                                    onClick={(e) => onItemSelect && onItemSelect(childItem.id, { shiftKey: e.shiftKey, orderedIds: visibleOrderedIds })}
                                >
                                    {hasListActions && (
                                        <TableCell $len="18px" $svgMargin="0">
                                            <SelectionCheckbox
                                                state={isChildSelected ? 'selected' : 'none'}
                                                onChange={(opts) => onItemSelect && onItemSelect(childItem.id, { ...opts, orderedIds: visibleOrderedIds })}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell $len="24px" $svgMargin="0" />
                                    {columns.map((col, j) => (
                                        <TableCell key={j} $len={col.width} $shrink={col.shrink}>
                                            <p data-testid={`${endpoint}.list.${parentIndex + 1}.child.${childIndex + 1}.${col.id}`}>
                                                <DisplayElement
                                                    string={resolve(col.id, childItem)}
                                                    type={col.type}
                                                    col={col}
                                                    item={childItem}
                                                />
                                            </p>
                                        </TableCell>
                                    ))}
                                    {lineActions && (
                                        <ActionsOverlay onClick={(e) => e.stopPropagation()}>
                                            <LineActions
                                                lineActions={lineActions}
                                                item={childItem}
                                                onAction={onAction}
                                            />
                                        </ActionsOverlay>
                                    )}
                                </ChildTableListItem>
                            )
                        })}
                    </div>
                )
            })}
        </TableList>
    )
}

export default TableRowsHierarchical
